import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { MapContainer, TileLayer, Polyline, Marker, Tooltip, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useLocation } from 'react-router-dom';

const API = 'https://app.b3rastreamento.com.br/api';

const iconInicio = L.divIcon({
  className: '',
  html: `<div style="background:#1D9E75;border:3px solid white;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 2px 8px rgba(0,0,0,0.3)">🟢</div>`,
  iconSize: [32, 32], iconAnchor: [16, 16],
});

const iconFim = L.divIcon({
  className: '',
  html: `<div style="background:#E24B4A;border:3px solid white;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 2px 8px rgba(0,0,0,0.3)">🔴</div>`,
  iconSize: [32, 32], iconAnchor: [16, 16],
});

const criarIconePonto = (velocidade) => L.divIcon({
  className: '',
  html: `<div style="background:${velocidade > 0 ? '#378ADD' : '#F59E0B'};border:2px solid white;border-radius:50%;width:12px;height:12px;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
  iconSize: [12, 12], iconAnchor: [6, 6],
});

function AjustarMapa({ pontos }) {
  const map = useMap();
  useEffect(() => {
    if (pontos.length > 0) {
      const bounds = L.latLngBounds(pontos.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [pontos, map]);
  return null;
}

async function snapToRoad(pontos) {
  if (pontos.length < 2) return pontos;
  try {
    const coords = pontos.map(p => `${p.lng},${p.lat}`).join(';');
    const url = `https://router.project-osrm.org/match/v1/driving/${coords}?overview=full&geometries=geojson&radiuses=${pontos.map(() => '25').join(';')}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.matchings && data.matchings.length > 0) {
      return data.matchings[0].geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
    }
    return pontos;
  } catch (e) { return pontos; }
}

async function buscarEnderecoNominatim(lat, lng) {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`https://app.b3rastreamento.com.br/api/endereco/reverso?lat=${lat}&lng=${lng}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    return data.endereco || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch {}
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

// Seleciona pontos a cada X minutos ou em eventos importantes
function selecionarPontosParaPDF(posicoes, intervaloMinutos = 5) {
  if (posicoes.length === 0) return [];
  const selecionados = [{ ...posicoes[0], motivo: 'Início' }];
  let ultimoTempo = new Date(posicoes[0].data).getTime();
  let ultimaVel = posicoes[0].velocidade;

  for (let i = 1; i < posicoes.length - 1; i++) {
    const p = posicoes[i];
    const tempo = new Date(p.data).getTime();
    const diffMin = (tempo - ultimoTempo) / 60000;
    const parou = ultimaVel > 5 && p.velocidade === 0;
    const saiu = ultimaVel === 0 && p.velocidade > 5;
    const excesso = p.velocidade > 80;

    if (diffMin >= intervaloMinutos || parou || saiu || excesso) {
      let motivo = '';
      if (parou) motivo = '⏸ Parada';
      else if (saiu) motivo = '▶ Retomou';
      else if (excesso) motivo = `⚡ ${p.velocidade} km/h`;
      else motivo = '📍 Rota';
      selecionados.push({ ...p, motivo });
      ultimoTempo = tempo;
    }
    ultimaVel = p.velocidade;
  }

  if (posicoes.length > 1) selecionados.push({ ...posicoes[posicoes.length - 1], motivo: 'Fim' });
  return selecionados;
}

function Relatorios() {
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [targets, setTargets] = useState([]);
  const [targetId, setTargetId] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [pontosRota, setPontosRota] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [progressoPDF, setProgressoPDF] = useState('');
  const [mostrarTodosPontos, setMostrarTodosPontos] = useState(false);
  const [intervalo, setIntervalo] = useState(5);
  const mapaRef = useRef(null);
  const location = useLocation();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    setDataInicio(ontem.toISOString().slice(0, 16));
    setDataFim(hoje.toISOString().slice(0, 16));
    axios.get(`${API}/targets`, { headers }).then(r => {
      setTargets(r.data);
      const params = new URLSearchParams(location.search);
      const targetParam = params.get('target');
      if (targetParam) setTargetId(targetParam);
    });
    axios.get(`${API}/auth/tenant`, { headers }).then(r => setTenant(r.data));
  }, []);

  const buscar = async () => {
    if (!targetId || !dataInicio || !dataFim) { alert('Selecione o veículo e o período!'); return; }
    setCarregando(true);
    setResultado(null);
    setPontosRota([]);
    try {
      const res = await axios.get(`${API}/targets/${targetId}/historico`, {
        headers, params: { data_inicio: dataInicio, data_fim: dataFim }
      });
      setResultado(res.data);
      if (res.data.posicoes.length > 1) {
        const lotes = [];
        for (let i = 0; i < res.data.posicoes.length; i += 100) lotes.push(res.data.posicoes.slice(i, i + 100));
        const rotasLotes = await Promise.all(lotes.map(l => snapToRoad(l)));
        setPontosRota(rotasLotes.flat());
      } else {
        setPontosRota(res.data.posicoes);
      }
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao buscar histórico'); }
    setCarregando(false);
  };

  const exportarCSV = () => {
    if (!resultado) return;
    const linhas = [
      'Data,Hora,Latitude,Longitude,Velocidade,Ignicao',
      ...resultado.posicoes.map(p => {
        const d = new Date(p.data);
        return `${d.toLocaleDateString('pt-BR')},${d.toLocaleTimeString('pt-BR')},${p.lat},${p.lng},${p.velocidade},${p.ignicao ? 'Sim' : 'Não'}`;
      })
    ];
    const blob = new Blob([linhas.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historico_${targetId}_${dataInicio.slice(0,10)}.csv`;
    a.click();
  };

  const exportarPDFRapido = async () => {
    if (!resultado) return;
    setGerandoPDF(true);
    setProgressoPDF('Gerando PDF...');
    try {
      const cor = tenant?.cor_primaria || '#1D9E75';
      const nomeTenant = tenant?.nome || 'Rastreamento';
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const w = 210;

      const gerarHeader = (pg, total) => {
        pdf.setFillColor(cor); pdf.rect(0, 0, w, 20, 'F');
        pdf.setTextColor(255,255,255); pdf.setFontSize(10); pdf.setFont('helvetica','bold');
        pdf.text(`${nomeTenant} — Relatório de Rota`, 14, 13);
        pdf.text(`Página ${pg} de ${total}`, w - 14, 13, { align: 'right' });
      };

      const gerarRodape = () => {
        pdf.setFillColor(245,245,245); pdf.rect(0, 282, w, 15, 'F');
        pdf.setTextColor(150,150,150); pdf.setFontSize(7); pdf.setFont('helvetica','normal');
        pdf.text('Desenvolvido por Digital Mais BR — digitalmaisbr.com.br', w/2, 290, { align: 'center' });
      };

      gerarHeader(1, 2);
      if (tenant?.logo_url) { try { pdf.addImage(tenant.logo_url, 'PNG', 10, 2, 30, 16); } catch {} }

      pdf.setTextColor(60,60,60); pdf.setFontSize(13); pdf.setFont('helvetica','bold');
      pdf.text(`${resultado.target.identificador} — ${resultado.target.nome}`, 14, 30);
      pdf.setFontSize(9); pdf.setFont('helvetica','normal'); pdf.setTextColor(100,100,100);
      pdf.text(`Período: ${new Date(dataInicio).toLocaleString('pt-BR')} até ${new Date(dataFim).toLocaleString('pt-BR')}`, 14, 37);
      pdf.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 43);

      const cards = [
        { label: 'Total de pontos', valor: String(resultado.total) },
        { label: 'Distância total', valor: `${resultado.distancia_km} km` },
        { label: 'Velocidade máxima', valor: `${resultado.velocidade_maxima} km/h` },
      ];
      cards.forEach((c, i) => {
        const x = 14 + i * 62;
        pdf.setFillColor(245,245,245); pdf.roundedRect(x, 48, 58, 20, 3, 3, 'F');
        pdf.setFillColor(cor); pdf.roundedRect(x, 48, 3, 20, 1, 1, 'F');
        pdf.setTextColor(60,60,60); pdf.setFontSize(13); pdf.setFont('helvetica','bold');
        pdf.text(c.valor, x+8, 60);
        pdf.setFontSize(7); pdf.setFont('helvetica','normal'); pdf.setTextColor(120,120,120);
        pdf.text(c.label, x+8, 65);
      });

      setProgressoPDF('Capturando mapa...');
      const mapaEl = document.querySelector('.leaflet-container');
      if (mapaEl) {
        const canvas = await html2canvas(mapaEl, { useCORS: true, scale: 1.5 });
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        pdf.setFontSize(10); pdf.setFont('helvetica','bold'); pdf.setTextColor(60,60,60);
        pdf.text('Mapa da Rota', 14, 77);
        pdf.addImage(imgData, 'JPEG', 14, 80, 182, 100);
      }
      gerarRodape();

      // Página 2 — tabela completa sem endereços
      pdf.addPage();
      gerarHeader(2, 2);
      pdf.setFontSize(11); pdf.setFont('helvetica','bold'); pdf.setTextColor(60,60,60);
      pdf.text('Registro Completo de Posições', 14, 30);
      pdf.setFontSize(8); pdf.setFont('helvetica','normal'); pdf.setTextColor(120,120,120);
      pdf.text(`Total: ${resultado.posicoes.length} registros`, 14, 36);

      let y = 42;
      pdf.setFillColor(cor); pdf.rect(14, y, 182, 8, 'F');
      pdf.setTextColor(255,255,255); pdf.setFontSize(7.5); pdf.setFont('helvetica','bold');
      pdf.text('Data', 16, y+5.5); pdf.text('Hora', 42, y+5.5); pdf.text('Latitude', 68, y+5.5);
      pdf.text('Longitude', 100, y+5.5); pdf.text('Velocidade', 132, y+5.5); pdf.text('Ignição', 163, y+5.5);
      y += 11;

      resultado.posicoes.forEach((p, i) => {
        if (y > 272) {
          gerarRodape(); pdf.addPage(); gerarHeader('2+', 2);
          y = 28;
          pdf.setFillColor(cor); pdf.rect(14, y, 182, 8, 'F');
          pdf.setTextColor(255,255,255); pdf.setFontSize(7.5); pdf.setFont('helvetica','bold');
          pdf.text('Data', 16, y+5.5); pdf.text('Hora', 42, y+5.5); pdf.text('Latitude', 68, y+5.5);
          pdf.text('Longitude', 100, y+5.5); pdf.text('Velocidade', 132, y+5.5); pdf.text('Ignição', 163, y+5.5);
          y += 11;
        }
        if (i % 2 === 0) { pdf.setFillColor(248,248,248); pdf.rect(14, y-5, 182, 7, 'F'); }
        const d = new Date(p.data);
        pdf.setTextColor(80,80,80); pdf.setFontSize(7); pdf.setFont('helvetica','normal');
        pdf.text(d.toLocaleDateString('pt-BR'), 16, y);
        pdf.text(d.toLocaleTimeString('pt-BR'), 42, y);
        pdf.text(String(p.lat.toFixed(6)), 68, y);
        pdf.text(String(p.lng.toFixed(6)), 100, y);
        if (p.velocidade > 80) { pdf.setTextColor(163,45,45); pdf.setFont('helvetica','bold'); }
        else if (p.velocidade > 0) { pdf.setTextColor(34,197,94); }
        pdf.text(`${p.velocidade} km/h`, 132, y);
        pdf.setFont('helvetica','normal');
        pdf.setTextColor(p.ignicao ? 15 : 163, p.ignicao ? 110 : 45, p.ignicao ? 86 : 45);
        pdf.text(p.ignicao ? '🟢 Ligada' : '🔴 Deslig.', 163, y);
        pdf.setTextColor(80,80,80);
        y += 7;
      });

      gerarRodape();
      pdf.save(`relatorio_${resultado.target.identificador}_${dataInicio.slice(0,10)}.pdf`);
      setProgressoPDF('');
    } catch (err) {
      console.error(err); alert('Erro ao gerar PDF');
      setProgressoPDF('');
    }
    setGerandoPDF(false);
  };

  const exportarPDF = async () => {
    if (!resultado) return;
    setGerandoPDF(true);
    try {
      const cor = tenant?.cor_primaria || '#1D9E75';
      const nomeTenant = tenant?.nome || 'Rastreamento';
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const w = 210;

      const gerarHeader = (pg, total) => {
        pdf.setFillColor(cor); pdf.rect(0, 0, w, 20, 'F');
        pdf.setTextColor(255,255,255); pdf.setFontSize(10); pdf.setFont('helvetica','bold');
        pdf.text(`${nomeTenant} — Relatório de Rota`, 14, 13);
        pdf.text(`Página ${pg} de ${total}`, w - 14, 13, { align: 'right' });
      };

      const gerarRodape = () => {
        pdf.setFillColor(245,245,245); pdf.rect(0, 282, w, 15, 'F');
        pdf.setTextColor(150,150,150); pdf.setFontSize(7); pdf.setFont('helvetica','normal');
        pdf.text('Desenvolvido por Digital Mais BR — digitalmaisbr.com.br', w/2, 290, { align: 'center' });
      };

      // ===== PÁGINA 1 — Resumo + Mapa =====
      gerarHeader(1, 3);

      if (tenant?.logo_url) {
        try { pdf.addImage(tenant.logo_url, 'PNG', 10, 2, 30, 16); } catch {}
      }

      pdf.setTextColor(60,60,60); pdf.setFontSize(13); pdf.setFont('helvetica','bold');
      pdf.text(`${resultado.target.identificador} — ${resultado.target.nome}`, 14, 30);
      pdf.setFontSize(9); pdf.setFont('helvetica','normal'); pdf.setTextColor(100,100,100);
      pdf.text(`Período: ${new Date(dataInicio).toLocaleString('pt-BR')} até ${new Date(dataFim).toLocaleString('pt-BR')}`, 14, 37);
      pdf.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 43);

      const cards = [
        { label: 'Total de pontos', valor: String(resultado.total) },
        { label: 'Distância total', valor: `${resultado.distancia_km} km` },
        { label: 'Velocidade máxima', valor: `${resultado.velocidade_maxima} km/h` },
      ];
      cards.forEach((c, i) => {
        const x = 14 + i * 62;
        pdf.setFillColor(245,245,245); pdf.roundedRect(x, 48, 58, 20, 3, 3, 'F');
        pdf.setFillColor(cor); pdf.roundedRect(x, 48, 3, 20, 1, 1, 'F');
        pdf.setTextColor(60,60,60); pdf.setFontSize(13); pdf.setFont('helvetica','bold');
        pdf.text(c.valor, x+8, 60);
        pdf.setFontSize(7); pdf.setFont('helvetica','normal'); pdf.setTextColor(120,120,120);
        pdf.text(c.label, x+8, 65);
      });

      setProgressoPDF('Capturando mapa...');
      const mapaEl = document.querySelector('.leaflet-container');
      if (mapaEl) {
        const canvas = await html2canvas(mapaEl, { useCORS: true, scale: 1.5 });
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        pdf.setFontSize(10); pdf.setFont('helvetica','bold'); pdf.setTextColor(60,60,60);
        pdf.text('Mapa da Rota', 14, 77);
        pdf.addImage(imgData, 'JPEG', 14, 80, 182, 100);

        pdf.setFontSize(8); pdf.setFont('helvetica','normal'); pdf.setTextColor(100,100,100);
        pdf.text('🟢 Início da rota    🔴 Fim da rota    🔵 Em movimento    🟡 Parado', 14, 185);
      }
      gerarRodape();

      // ===== PÁGINA 2 — Pontos com endereços =====
      pdf.addPage();
      gerarHeader(2, 3);

      pdf.setFontSize(11); pdf.setFont('helvetica','bold'); pdf.setTextColor(60,60,60);
      pdf.text('Detalhamento de Rota com Endereços', 14, 30);
      pdf.setFontSize(8); pdf.setFont('helvetica','normal'); pdf.setTextColor(120,120,120);
      pdf.text(`Um ponto a cada ${intervalo} minuto(s) + paradas e eventos importantes`, 14, 36);

      const pontosParaPDF = selecionarPontosParaPDF(resultado.posicoes, intervalo);
      setProgressoPDF(`Buscando endereços (0/${pontosParaPDF.length})...`);

      // Busca endereços em paralelo (lotes de 5 simultâneos)
      const enderecos = new Array(pontosParaPDF.length).fill('');
      const tamLote = 5;
      for (let i = 0; i < pontosParaPDF.length; i += tamLote) {
        const lote = pontosParaPDF.slice(i, i + tamLote);
        setProgressoPDF(`Buscando endereços (${Math.min(i + tamLote, pontosParaPDF.length)}/${pontosParaPDF.length})...`);
        const resultados = await Promise.all(
          lote.map(p => buscarEnderecoNominatim(p.lat, p.lng))
        );
        resultados.forEach((end, j) => { enderecos[i + j] = end; });
        if (i + tamLote < pontosParaPDF.length) {
          await new Promise(r => setTimeout(r, 1000)); // pausa entre lotes
        }
      }

      // Cabeçalho tabela
      let y = 42;
      pdf.setFillColor(cor); pdf.rect(14, y, 182, 8, 'F');
      pdf.setTextColor(255,255,255); pdf.setFontSize(7.5); pdf.setFont('helvetica','bold');
      pdf.text('#', 16, y+5.5);
      pdf.text('Data', 24, y+5.5);
      pdf.text('Hora', 46, y+5.5);
      pdf.text('Endereço / Local', 65, y+5.5);
      pdf.text('Vel.', 152, y+5.5);
      pdf.text('Ign.', 164, y+5.5);
      pdf.text('Evento', 176, y+5.5);
      y += 11;

      pontosParaPDF.forEach((p, i) => {
        if (y > 270) { // nova página se necessário
          gerarRodape();
          pdf.addPage();
          gerarHeader('2+', 3);
          y = 28;
          pdf.setFillColor(cor); pdf.rect(14, y, 182, 8, 'F');
          pdf.setTextColor(255,255,255); pdf.setFontSize(7.5); pdf.setFont('helvetica','bold');
          pdf.text('#', 16, y+5.5); pdf.text('Data', 24, y+5.5); pdf.text('Hora', 46, y+5.5);
          pdf.text('Endereço / Local', 65, y+5.5); pdf.text('Vel.', 152, y+5.5);
          pdf.text('Ign.', 164, y+5.5); pdf.text('Evento', 176, y+5.5);
          y += 11;
        }

        if (i % 2 === 0) { pdf.setFillColor(248,248,248); pdf.rect(14, y-5, 182, 11, 'F'); }

        const d = new Date(p.data);
        const isInicio = p.motivo === 'Início';
        const isFim = p.motivo === 'Fim';

        pdf.setTextColor(80,80,80); pdf.setFontSize(7); pdf.setFont('helvetica','normal');
        pdf.text(String(i+1), 16, y);
        pdf.text(d.toLocaleDateString('pt-BR'), 24, y);
        pdf.text(d.toLocaleTimeString('pt-BR'), 46, y);

        const endStr = enderecos[i] || '';
        const maxChar = 48;
        if (endStr.length > maxChar) {
          pdf.text(endStr.substring(0, maxChar), 65, y-2);
          pdf.text(endStr.substring(maxChar, maxChar+46), 65, y+3);
        } else {
          pdf.text(endStr, 65, y);
        }

        if (p.velocidade > 80) { pdf.setTextColor(163,45,45); pdf.setFont('helvetica','bold'); }
        else if (p.velocidade > 0) { pdf.setTextColor(34,197,94); }
        else { pdf.setTextColor(100,100,100); }
        pdf.text(`${p.velocidade}km/h`, 152, y);

        pdf.setFont('helvetica','normal');
        pdf.setTextColor(p.ignicao ? 15 : 163, p.ignicao ? 110 : 45, p.ignicao ? 86 : 45);
        pdf.text(p.ignicao ? 'Lig.' : 'Des.', 164, y);

        if (isInicio) pdf.setTextColor(15,110,86);
        else if (isFim) pdf.setTextColor(163,45,45);
        else if (p.motivo?.includes('Parada')) pdf.setTextColor(99,56,6);
        else if (p.motivo?.includes('km/h')) pdf.setTextColor(163,45,45);
        else pdf.setTextColor(55,138,221);
        pdf.setFont('helvetica','bold');
        pdf.text(p.motivo || '📍', 176, y);
        pdf.setFont('helvetica','normal'); pdf.setTextColor(80,80,80);
        y += 12;
      });

      gerarRodape();

      // ===== PÁGINA 3 — Tabela completa =====
      pdf.addPage();
      gerarHeader(3, 3);

      pdf.setFontSize(11); pdf.setFont('helvetica','bold'); pdf.setTextColor(60,60,60);
      pdf.text('Registro Completo de Posições', 14, 30);
      pdf.setFontSize(8); pdf.setFont('helvetica','normal'); pdf.setTextColor(120,120,120);
      pdf.text(`Total: ${resultado.posicoes.length} registros`, 14, 36);

      y = 42;
      pdf.setFillColor(cor); pdf.rect(14, y, 182, 8, 'F');
      pdf.setTextColor(255,255,255); pdf.setFontSize(7.5); pdf.setFont('helvetica','bold');
      pdf.text('Data', 16, y+5.5); pdf.text('Hora', 42, y+5.5); pdf.text('Latitude', 68, y+5.5);
      pdf.text('Longitude', 100, y+5.5); pdf.text('Velocidade', 132, y+5.5); pdf.text('Ignição', 163, y+5.5);
      y += 11;

      resultado.posicoes.forEach((p, i) => {
        if (y > 272) {
          gerarRodape();
          pdf.addPage();
          gerarHeader('3+', 3);
          y = 28;
          pdf.setFillColor(cor); pdf.rect(14, y, 182, 8, 'F');
          pdf.setTextColor(255,255,255); pdf.setFontSize(7.5); pdf.setFont('helvetica','bold');
          pdf.text('Data', 16, y+5.5); pdf.text('Hora', 42, y+5.5); pdf.text('Latitude', 68, y+5.5);
          pdf.text('Longitude', 100, y+5.5); pdf.text('Velocidade', 132, y+5.5); pdf.text('Ignição', 163, y+5.5);
          y += 11;
        }
        if (i % 2 === 0) { pdf.setFillColor(248,248,248); pdf.rect(14, y-5, 182, 7, 'F'); }
        const d = new Date(p.data);
        pdf.setTextColor(80,80,80); pdf.setFontSize(7); pdf.setFont('helvetica','normal');
        pdf.text(d.toLocaleDateString('pt-BR'), 16, y);
        pdf.text(d.toLocaleTimeString('pt-BR'), 42, y);
        pdf.text(String(p.lat.toFixed(6)), 68, y);
        pdf.text(String(p.lng.toFixed(6)), 100, y);
        if (p.velocidade > 80) { pdf.setTextColor(163,45,45); pdf.setFont('helvetica','bold'); }
        else if (p.velocidade > 0) { pdf.setTextColor(34,197,94); }
        pdf.text(`${p.velocidade} km/h`, 132, y);
        pdf.setFont('helvetica','normal');
        pdf.setTextColor(p.ignicao ? 15 : 163, p.ignicao ? 110 : 45, p.ignicao ? 86 : 45);
        pdf.text(p.ignicao ? '🟢 Ligada' : '🔴 Deslig.', 163, y);
        pdf.setTextColor(80,80,80);
        y += 7;
      });

      gerarRodape();
      pdf.save(`relatorio_${resultado.target.identificador}_${dataInicio.slice(0,10)}.pdf`);
      setProgressoPDF('');
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      alert('Erro ao gerar PDF');
      setProgressoPDF('');
    }
    setGerandoPDF(false);
  };

  const centro = pontosRota.length > 0
    ? [pontosRota[Math.floor(pontosRota.length/2)].lat, pontosRota[Math.floor(pontosRota.length/2)].lng]
    : [-23.3045, -51.1696];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
      <Sidebar aberta={sidebarAberta} setAberta={setSidebarAberta} />

      <div style={{ background: tenant?.cor_primaria || '#1D9E75', padding: '0 24px', height: '60px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 500, boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => setSidebarAberta(!sidebarAberta)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: 'white' }}>{sidebarAberta ? '✕' : '☰'}</button>
          <span style={{ color: 'white', fontWeight: '700', fontSize: '18px' }}>📍 Relatório de Rotas</span>
        </div>
      </div>

      <div style={{ marginTop: '60px', display: 'flex', height: 'calc(100vh - 60px)' }}>
        <div style={{ width: '320px', background: 'white', borderRight: '1px solid #e0e0e0', padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>VEÍCULO</label>
            <select value={targetId} onChange={e => setTargetId(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', background: 'white' }}>
              <option value="">Selecione...</option>
              {targets.map(t => <option key={t.id} value={t.id}>{t.icone} {t.identificador} — {t.nome}</option>)}
            </select>
          </div>

          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>DATA INÍCIO</label>
            <input type="datetime-local" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} />
          </div>

          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>DATA FIM</label>
            <input type="datetime-local" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} />
          </div>

          <div>
            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600', display: 'block', marginBottom: '6px' }}>INTERVALO NO PDF (minutos)</label>
            <select value={intervalo} onChange={e => setIntervalo(Number(e.target.value))} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', background: 'white' }}>
              <option value={1}>A cada 1 minuto (mais detalhado)</option>
              <option value={2}>A cada 2 minutos</option>
              <option value={5}>A cada 5 minutos</option>
              <option value={10}>A cada 10 minutos</option>
              <option value={15}>A cada 15 minutos</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#333', cursor: 'pointer' }}>
              <input type="checkbox" checked={mostrarTodosPontos} onChange={e => setMostrarTodosPontos(e.target.checked)} />
              Mostrar todos os pontos no mapa
            </label>
          </div>

          <button onClick={buscar} disabled={carregando} style={{ background: carregando ? '#ccc' : (tenant?.cor_primaria || '#1D9E75'), color: 'white', border: 'none', borderRadius: '8px', padding: '12px', cursor: carregando ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px' }}>
            {carregando ? '⏳ Buscando...' : '🔍 Buscar Rota'}
          </button>

          {resultado && (
            <>
              <div style={{ background: '#f8f8f8', borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '10px' }}>
                  {resultado.target.identificador} — {resultado.target.nome}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { label: 'Pontos', valor: resultado.total, cor: tenant?.cor_primaria || '#1D9E75' },
                    { label: 'Distância', valor: `${resultado.distancia_km} km`, cor: '#378ADD' },
                    { label: 'Vel. máx', valor: `${resultado.velocidade_maxima} km/h`, cor: '#E24B4A' },
                  ].map((c, i) => (
                    <div key={i} style={{ background: 'white', borderRadius: '8px', padding: '10px', borderLeft: `3px solid ${c.cor}` }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: c.cor }}>{c.valor}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>{c.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={() => exportarPDFRapido()} disabled={gerandoPDF} style={{ background: gerandoPDF ? '#ccc' : '#E24B4A', color: 'white', border: 'none', borderRadius: '8px', padding: '12px', cursor: gerandoPDF ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' }}>
                {gerandoPDF ? `⏳ ${progressoPDF}` : '📄 Exportar PDF (rápido)'}
              </button>

              <button onClick={() => exportarPDF()} disabled={gerandoPDF} style={{ background: gerandoPDF ? '#ccc' : '#856404', color: 'white', border: 'none', borderRadius: '8px', padding: '10px', cursor: gerandoPDF ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '12px' }}>
                {gerandoPDF ? `⏳ ${progressoPDF}` : '📄 PDF com endereços (mais lento)'}
              </button>

              <button onClick={exportarCSV} style={{ background: '#E6F1FB', color: '#185FA5', border: 'none', borderRadius: '8px', padding: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                📥 Exportar CSV
              </button>

              <div style={{ background: '#f0f8ff', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#185FA5' }}>
                💡 Clique nos pontos do mapa para ver horário, velocidade e abrir Street View.
              </div>

              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#f8f8f8' }}>
                    <tr>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: '#666' }}>Hora</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: '#666' }}>Vel.</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: '#666' }}>Ign.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.posicoes.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '5px 8px', color: '#333' }}>{new Date(p.data).toLocaleTimeString('pt-BR')}</td>
                        <td style={{ padding: '5px 8px', color: p.velocidade > 0 ? '#22C55E' : '#666', fontWeight: '600' }}>{p.velocidade} km/h</td>
                        <td style={{ padding: '5px 8px' }}>{p.ignicao ? '🟢' : '🔴'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div style={{ flex: 1 }} ref={mapaRef}>
          <MapContainer center={centro} zoom={13} style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            {pontosRota.length > 1 && (
              <>
                <Polyline positions={pontosRota.map(p => [p.lat, p.lng])} color={tenant?.cor_primaria || '#1D9E75'} weight={4} opacity={0.8} />
                <AjustarMapa pontos={pontosRota} />
              </>
            )}

            {/* Marcador início */}
            {resultado?.posicoes?.length > 0 && (
              <Marker position={[resultado.posicoes[0].lat, resultado.posicoes[0].lng]} icon={iconInicio}>
                <Popup>
                  <div style={{ fontSize: '13px', minWidth: '180px' }}>
                    <strong>🟢 Início da rota</strong><br/>
                    🕐 {new Date(resultado.posicoes[0].data).toLocaleString('pt-BR')}<br/>
                    ⚡ {resultado.posicoes[0].velocidade} km/h<br/>
                    <a href={`https://www.google.com/maps?layer=c&cbll=${resultado.posicoes[0].lat},${resultado.posicoes[0].lng}`} target="_blank" rel="noreferrer"
                      style={{ color: '#1D9E75', fontWeight: '600', display: 'block', marginTop: '6px' }}>
                      🌍 Ver no Street View
                    </a>
                    <a href={`https://www.google.com/maps?q=${resultado.posicoes[0].lat},${resultado.posicoes[0].lng}`} target="_blank" rel="noreferrer"
                      style={{ color: '#378ADD', fontWeight: '600', display: 'block', marginTop: '4px' }}>
                      🗺️ Ver no Google Maps
                    </a>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Marcador fim */}
            {resultado?.posicoes?.length > 1 && (
              <Marker position={[resultado.posicoes[resultado.posicoes.length-1].lat, resultado.posicoes[resultado.posicoes.length-1].lng]} icon={iconFim}>
                <Popup>
                  <div style={{ fontSize: '13px', minWidth: '180px' }}>
                    <strong>🔴 Fim da rota</strong><br/>
                    🕐 {new Date(resultado.posicoes[resultado.posicoes.length-1].data).toLocaleString('pt-BR')}<br/>
                    ⚡ {resultado.posicoes[resultado.posicoes.length-1].velocidade} km/h<br/>
                    <a href={`https://www.google.com/maps?layer=c&cbll=${resultado.posicoes[resultado.posicoes.length-1].lat},${resultado.posicoes[resultado.posicoes.length-1].lng}`} target="_blank" rel="noreferrer"
                      style={{ color: '#1D9E75', fontWeight: '600', display: 'block', marginTop: '6px' }}>
                      🌍 Ver no Street View
                    </a>
                    <a href={`https://www.google.com/maps?q=${resultado.posicoes[resultado.posicoes.length-1].lat},${resultado.posicoes[resultado.posicoes.length-1].lng}`} target="_blank" rel="noreferrer"
                      style={{ color: '#378ADD', fontWeight: '600', display: 'block', marginTop: '4px' }}>
                      🗺️ Ver no Google Maps
                    </a>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Pontos intermediários clicáveis */}
            {mostrarTodosPontos && resultado?.posicoes?.slice(1, -1).filter((_, i) => i % 3 === 0).map((p, i) => (
              <Marker key={i} position={[p.lat, p.lng]} icon={criarIconePonto(p.velocidade)}>
                <Popup>
                  <div style={{ fontSize: '12px', minWidth: '180px' }}>
                    <strong>📍 Ponto {i+1}</strong><br/>
                    🕐 {new Date(p.data).toLocaleString('pt-BR')}<br/>
                    ⚡ {p.velocidade} km/h<br/>
                    🔑 Ignição: {p.ignicao ? '🟢 Ligada' : '🔴 Desligada'}<br/>
                    <a href={`https://www.google.com/maps?layer=c&cbll=${p.lat},${p.lng}`} target="_blank" rel="noreferrer"
                      style={{ color: '#1D9E75', fontWeight: '600', display: 'block', marginTop: '6px' }}>
                      🌍 Ver no Street View
                    </a>
                    <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer"
                      style={{ color: '#378ADD', fontWeight: '600', display: 'block', marginTop: '4px' }}>
                      🗺️ Ver no Google Maps
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

export default Relatorios;